import Component from '@glimmer/component';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';

import type RoomModel from 'admin-panel/models/room';

type DragSelection = {
  initialRoomIds: Set<number>;
  lastRoomIds: Set<number>;
  pointerId: number;
  rooms: Array<[number, DOMRect]>;
  startX: number;
  startY: number;
};

interface PanelCategoryRoomGridArgs {
  rooms: RoomModel[];
  selectedRoomIds: number[];
  onChange?: (roomIds: number[]) => void;
}

export default class PanelCategoryRoomGridComponent extends Component<PanelCategoryRoomGridArgs> {
  private dragSelection: DragSelection | null = null;
  private readonly inputIdPrefix = `${guidFor(this)}-room`;

  get roomOptions() {
    return roomOptionsFor(
      this.inputIdPrefix,
      this.args.rooms,
      this.args.selectedRoomIds
    );
  }

  get validRoomIds() {
    return this.roomOptions.map((option) => option.id);
  }

  get areAllRoomsSelected() {
    const selectedRoomIds = new Set(this.args.selectedRoomIds);

    return this.validRoomIds.every((id) => selectedRoomIds.has(id));
  }

  get areNoRoomsSelected() {
    return this.args.selectedRoomIds.length === 0;
  }

  @action
  toggleRoom(id: number, event: Event) {
    const checkbox = event.currentTarget;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const selectedRoomIds = checkbox.checked
      ? [...new Set([...this.args.selectedRoomIds, id])]
      : this.args.selectedRoomIds.filter((roomId) => roomId !== id);

    this.args.onChange?.(selectedRoomIds);
  }

  @action
  beginDragSelection(event: PointerEvent) {
    const panel = event.currentTarget;
    const target = event.target;
    if (!(panel instanceof HTMLDivElement) || !(target instanceof Element))
      return;
    if (target.closest('button, label')) return;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    event.preventDefault();

    const initialRoomIds = new Set(this.args.selectedRoomIds);

    this.dragSelection = {
      initialRoomIds,
      lastRoomIds: new Set(initialRoomIds),
      pointerId: event.pointerId,
      rooms: Array.from(
        panel.querySelectorAll<HTMLElement>('[data-room-id]'),
        (element) => [
          Number(element.dataset['roomId']),
          element.getBoundingClientRect(),
        ]
      ),
      startX: event.clientX,
      startY: event.clientY,
    };

    panel.setPointerCapture(event.pointerId);
  }

  @action
  updateDragSelection(event: PointerEvent) {
    const dragSelection = this.dragSelection;
    if (!dragSelection || event.pointerId !== dragSelection.pointerId) return;

    event.preventDefault();

    const selectedRoomIds = new Set(dragSelection.initialRoomIds);
    const bottom = Math.max(dragSelection.startY, event.clientY);
    const left = Math.min(dragSelection.startX, event.clientX);
    const right = Math.max(dragSelection.startX, event.clientX);
    const top = Math.min(dragSelection.startY, event.clientY);
    let selectionChanged = false;

    for (const [id, room] of dragSelection.rooms) {
      if (
        left <= room.right &&
        right >= room.left &&
        top <= room.bottom &&
        bottom >= room.top
      ) {
        selectedRoomIds.has(id)
          ? selectedRoomIds.delete(id)
          : selectedRoomIds.add(id);
      }

      if (selectedRoomIds.has(id) !== dragSelection.lastRoomIds.has(id)) {
        selectionChanged = true;
      }
    }

    if (!selectionChanged) return;

    dragSelection.lastRoomIds = selectedRoomIds;
    this.args.onChange?.([...selectedRoomIds]);
  }

  @action
  endDragSelection(event: PointerEvent) {
    if (event.pointerId !== this.dragSelection?.pointerId) return;

    const panel = event.currentTarget;
    if (
      panel instanceof HTMLDivElement &&
      panel.hasPointerCapture(event.pointerId)
    ) {
      panel.releasePointerCapture(event.pointerId);
    }

    this.dragSelection = null;
  }

  @action
  selectAll() {
    this.args.onChange?.(this.validRoomIds);
  }

  @action
  deselectAll() {
    this.args.onChange?.([]);
  }
}

export function roomOptionsFor(
  inputIdPrefix: string,
  rooms: RoomModel[],
  roomIds: number[]
) {
  const selectedRoomIds = new Set(roomIds);

  return rooms.flatMap((room) => {
    const id = Number(room.id);

    if (!Number.isFinite(id)) return [];

    return [
      {
        id,
        inputId: `${inputIdPrefix}-${id}`,
        isSelected: selectedRoomIds.has(id),
        room,
      },
    ];
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::RoomGrid': typeof PanelCategoryRoomGridComponent;
    'panel/category/room-grid': typeof PanelCategoryRoomGridComponent;
  }
}
